/* Org.tsx — the org charts.
 *
 * A genuine node-link tree, not department columns, and its edge semantics ARE the feature: solid
 * where exactly one person sits on the level above in that department at that site, dashed where it
 * is one of N. There are no reporting lines in the source, so an edge this chart cannot justify is
 * not drawn — see org/tree.ts, which holds the build and the tidy layout unchanged.
 *
 * COMPANY view = the corporate layer only. A whole enterprise is not a drawable chart: Ford is 8,248
 * reachable people across 67 sites, and level 6+ is 4.2% of them. Director and below live at the
 * PLANTS, which is what the site index at the foot of the company chart is for.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, isErr } from '../lib/api.ts';
import { N, SQ, LVLNAME, ROLE_SHORT, ROLE_COLOR } from '../lib/fmt.ts';
import { Btn, Chip, ErrBox, Hint, Meter, Section, Seg, HAIR, selStyle } from '../components/ui.tsx';
import {
  buildTree, initials, keyOf, layout, NODE_W, nodeH, restoreOpen, walkAll, wrapTitle, clipTo,
  type Node, type Tree,
} from './org/tree.ts';

const LS = 'atlas-org-v1';

interface SearchHit {
  accounts: { domain: string; name: string; tier: string; facilities: number; buyers: number; reach: number; committee: number }[];
  plants: { id: number; site: string | null; company: string; city: string | null; state: string | null; reach: number; committee: number }[];
}

export function Org({ initial, onOpenPerson }: {
  initial?: { scope: 'acct' | 'fac'; key: string } | null;
  onOpenPerson?: (id: number) => void;
}) {
  const [scope, setScope] = useState<'acct' | 'fac'>(initial?.scope || 'acct');
  const [key, setKey] = useState<string | null>(initial?.key ?? null);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<SearchHit | null>(null);
  const [lens, setLens] = useState('all');
  const [minLevel, setMinLevel] = useState<number>(() => Number(localStorage.getItem(LS + '-min') || 6));
  const [showGhosts, setShowGhosts] = useState(() => localStorage.getItem(LS + '-ghosts') === '1');
  const [depts, setDepts] = useState<Set<string>>(new Set());
  const [expand, setExpand] = useState<Record<string, unknown>>({});
  const [t, setT] = useState<Tree | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const openState = useRef<Record<string, boolean>>({});
  const [view, setView] = useState({ k: 1, tx: 0, ty: 22 });
  const [nudge, setNudge] = useState(0);      // forces a re-layout after an open/collapse
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);

  useEffect(() => { localStorage.setItem(LS + '-min', String(minLevel)); }, [minLevel]);
  useEffect(() => { localStorage.setItem(LS + '-ghosts', showGhosts ? '1' : '0'); }, [showGhosts]);

  /* Debounced, and the token keeps a slow early reply off a newer list. One search populates the
     picker, and while nothing is selected its top account is also the DEFAULT chart — data-driven,
     never a hardcoded domain. The old "ducommun.com" default was a landmine the day that account
     left the SOM. */
  const token = useRef(0);
  useEffect(() => {
    const mine = ++token.current;
    const id = setTimeout(() => {
      api<SearchHit>('search?q=' + encodeURIComponent(q)).then((r) => {
        if (mine !== token.current || isErr(r)) return;
        setHits(r);
        if (!key && r.accounts.length) { setScope('acct'); setKey(r.accounts[0].domain); }
      });
    }, q ? 220 : 0);
    return () => clearTimeout(id);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!key) return;
    let dead = false;
    setErr(null);
    // Company = the corporate layer only; the plant view asks for everybody in that building.
    const min = scope === 'acct' ? `&minlevel=${minLevel}` : '';
    api<Tree>(`orgtree?scope=${scope}&key=${encodeURIComponent(key)}&lens=${lens}${min}`).then((r) => {
      if (dead) return;
      if (isErr(r)) { setErr(r.error); return; }     // the last good tree stays on screen
      /* The ONE place open/collapse state resets: picking a different company or plant. A dept
         filter or the ghost toggle re-renders and must keep what the user expanded. */
      openState.current = {};
      setExpand({}); setDepts(new Set());
      setT(r);
    });
    return () => { dead = true; };
  }, [scope, key, lens, minLevel]);

  const root = useMemo(() => {
    if (!t) return null;
    const r = buildTree(t, { expand, depts, showGhosts });
    restoreOpen(r, openState.current);
    return r;
    // `nudge` is in the dep list because open/collapse mutates node.open in place.
  }, [t, expand, depts, showGhosts, nudge]);

  const { nodes, edges } = useMemo(() => (root ? layout(root) : { nodes: [], edges: [] }), [root, nudge]);

  const fitChart = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap || !nodes.length) return;
    const w = wrap.clientWidth, hh = wrap.clientHeight;
    let x0 = 1e9, x1 = -1e9, y1 = -1e9;
    for (const n of nodes) { x0 = Math.min(x0, n.cx - NODE_W / 2); x1 = Math.max(x1, n.cx + NODE_W / 2); y1 = Math.max(y1, n.y + nodeH(n)); }
    const tw = Math.max(1, x1 - x0), th = Math.max(1, y1 + 20);
    /* Never shrink past the point where the job title stops being readable — legibility at a
       high-level view is the requirement, and a chart auto-zoomed to 20% satisfies "it fits" and
       nothing else. The floor is two-thirds because below that the email and phone lines on the
       cards stop being readable, which defeats the reason they are on the card. */
    const k = Math.min(1.25, Math.max(0.66, Math.min((w - 48) / tw, (hh - 48) / th)));
    /* Centre only when the whole tree fits. Once it is wider than the pane — which is most plants —
       centring slices the first and last cards by the same amount, and a card cut in half at the
       LEFT edge reads as a broken render rather than "there is more this way". */
    const tx = tw * k > w - 48 ? 24 - x0 * k : (w - tw * k) / 2 - x0 * k;
    /* A short tree sits a third of the way down rather than dead centre, capped at 90px: a collapsed
       two-row chart landing 200px from the top with 440px of nothing under it read as floating.
       Expansion grows downward, so the space belongs below the tree. */
    const ty = th * k < hh - 60 ? Math.min(90, Math.max(24, (hh - th * k) / 3)) : 22;
    setView({ k, tx, ty });
  }, [nodes]);

  useEffect(() => { fitChart(); }, [t, depts, showGhosts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wheel zoom about the pointer, wired by hand because React's onWheel is passive.
  useEffect(() => {
    const wrap = wrapRef.current; if (!wrap) return;
    const on = (e: WheelEvent) => {
      e.preventDefault();
      const r = wrap.getBoundingClientRect(), px = e.clientX - r.left, py = e.clientY - r.top;
      setView((v) => {
        const k1 = Math.max(0.12, Math.min(2.6, v.k * Math.exp(-e.deltaY * 0.0014)));
        return { k: k1, tx: px - (px - v.tx) * (k1 / v.k), ty: py - (py - v.ty) * (k1 / v.k) };
      });
    };
    wrap.addEventListener('wheel', on, { passive: false });
    return () => wrap.removeEventListener('wheel', on);
  }, []);

  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!drag.current) return;
      const d = drag.current;
      setView((v) => ({ ...v, tx: d.tx + (e.clientX - d.x), ty: d.ty + (e.clientY - d.y) }));
    };
    const up = () => { drag.current = null; };
    addEventListener('mousemove', move); addEventListener('mouseup', up);
    return () => { removeEventListener('mousemove', move); removeEventListener('mouseup', up); };
  }, []);

  const onNode = (n: Node) => {
    if (n.kind === 'more' && n.exp) { setExpand((p) => ({ ...p, [n.exp!]: 1 })); return; }
    if (n.p) { onOpenPerson?.(n.p.person_id); return; }
    if (n.children.length) {
      /* Anchor the node you clicked. Expanding re-runs the tidy layout, which re-centres every
         parent — without this the branch you just opened slides off the side of the pane and you
         have to go hunting for it. */
      const k = keyOf(n), sx0 = n.cx * view.k + view.tx, sy0 = n.y * view.k + view.ty;
      n.open = n.open === false;
      openState.current[k] = n.open;
      setNudge((x) => x + 1);
      // The re-layout happens synchronously in the memo below this render, so re-anchor next frame.
      requestAnimationFrame(() => {
        const after = (root ? layout(root).nodes : []).find((x) => keyOf(x) === k);
        if (after) setView((v) => ({ ...v, tx: sx0 - after.cx * v.k, ty: sy0 - after.y * v.k }));
      });
    }
  };

  const head = (t?.head || {}) as Record<string, unknown>;
  const ghosts = t ? t.stats.headcount - t.stats.shown : 0;

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      {/* ── picker ───────────────────────────────────────────────────────────────────────────── */}
      <aside style={{ width: 300, flex: '0 0 300px', borderRight: HAIR, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Section label="Pick a company or a plant">
          <input style={selStyle} placeholder="Ford, Ducommun, Wichita…" value={q} onChange={(e) => setQ(e.target.value)} />
          <div style={{ marginTop: 8 }}>
            <Seg wide value={scope} onPick={(s) => { setScope(s); setKey(null); }}
              options={[['acct', 'Company'], ['fac', 'Single plant']]} />
          </div>
        </Section>
        <div style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
          {!hits ? <Hint style={{ padding: 'var(--gut)' }}>searching…</Hint>
            : scope === 'acct'
              ? hits.accounts.map((a) => (
                <div key={a.domain} onClick={() => setKey(a.domain)}
                  style={{ padding: '7px var(--gut)', borderBottom: HAIR, cursor: 'pointer', background: key === a.domain ? 'rgba(255,170,0,.043)' : undefined }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                    <span style={{ fontWeight: 600 }}>{a.name}</span>
                    <Chip tone={a.tier === '1' ? 'warn' : 'none'}>T{a.tier}</Chip>
                  </div>
                  <Hint>{N(a.facilities)} plants · {N(a.buyers)} buyers · {N(a.reach)} reachable · {a.committee}/4</Hint>
                </div>
              ))
              : hits.plants.map((p) => (
                <div key={p.id} onClick={() => setKey(String(p.id))}
                  style={{ padding: '7px var(--gut)', borderBottom: HAIR, cursor: 'pointer', background: key === String(p.id) ? 'rgba(255,170,0,.043)' : undefined }}>
                  <div style={{ fontWeight: 600 }}>{p.site || p.company}</div>
                  <Hint>{p.city || ''}, {p.state || ''} · {p.company} · {N(p.reach)} reachable · {p.committee}/4</Hint>
                </div>
              ))}
          {hits && (scope === 'acct' ? hits.accounts : hits.plants).length === 0 && <Hint style={{ padding: 'var(--gut)' }}>no matches</Hint>}
        </div>
      </aside>

      {/* ── chart ────────────────────────────────────────────────────────────────────────────── */}
      <section style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {err && <ErrBox what="The org chart did not load" msg={err} />}
        {t && (
          <div style={{ padding: 'var(--gut)', borderBottom: HAIR, flex: '0 0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
              <div>
                <div className="t-label">{t.scope === 'acct' ? 'Company org' : 'Plant org'}</div>
                <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700 }}>
                  {t.scope === 'acct' ? String(head.name || t.key) : String(head.site || 'plant ' + t.key)}
                </div>
                <Hint>
                  {t.scope === 'acct'
                    ? `${head.domain || ''} · ${N(head.facilities)} plants · ${N(head.people)} people on file · ${N(head.reach)} reachable buyers`
                    : `${head.company || ''} · ${head.city || ''}, ${head.state || ''} · ${SQ(head.sqft)} sq ft · ${N(head.reach)} reachable`}
                </Hint>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                {t.scope === 'acct' && (
                  <div>
                    <div className="t-label" style={{ marginBottom: 3 }}>Depth</div>
                    <Seg value={String(minLevel)} onPick={(v) => setMinLevel(Number(v))}
                      options={[['7', 'C-suite'], ['6', 'VP+'], ['5', 'Director+']]} />
                  </div>
                )}
                <div>
                  <div className="t-label" style={{ marginBottom: 3 }}>Show</div>
                  <Seg value={t.lens} onPick={setLens} options={[
                    ['all', 'Everyone we hold'], ['committee', 'Buying committee'],
                    ['dm', 'DMs + sponsors'], ['reach', 'Reachable only'],
                  ]} />
                </div>
              </div>
            </div>

            {/* Department filter. Multi-select: click to add a function, click again to drop it,
                "All" clears. No reload — the tree is already in memory, so this is a rebuild. */}
            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
              <span className="t-label" style={{ marginRight: 4 }}>Department</span>
              <Btn sm on={depts.size === 0} onClick={() => setDepts(new Set())}>All</Btn>
              {t.depts.slice().sort((a, b) => b.shown - a.shown).map((d) => (
                <Btn key={d.dept} sm on={depts.has(d.dept)} onClick={() => {
                  const next = new Set(depts);
                  if (next.has(d.dept)) next.delete(d.dept); else next.add(d.dept);
                  setDepts(next);
                }}>{d.dept} <span className="num" style={{ opacity: 0.55 }}>{N(d.shown)}</span></Btn>
              ))}
            </div>

            {/* The edge key states the chart's own scope. A chart that silently drops 98% of an org
                reads as the whole org. */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10 }}>
              <Hint><EdgeSwatch /> manager known: one person on the level above ({N(t.stats.solid)})</Hint>
              <Hint><EdgeSwatch dashed /> one of several above them, no line drawn ({N(t.stats.dashed)})</Hint>
              <Hint>
                {t.scope === 'acct'
                  ? `Corporate layer only — ${LVLNAME[t.minLevel] || 'level ' + t.minLevel} and above. ${N(t.stats.shown)} of ${N(t.reachableAll)} reachable people at this company. Director and below live at the plants, in the index below.`
                  : `Everyone we hold in this building — ${N(t.stats.shown)} drawn of ${N(t.stats.headcount)} on file.`}
              </Hint>
              {t.stats.acting > 0 && (
                <Hint style={{ color: 'var(--am)' }}>
                  {t.stats.acting} acting head{t.stats.acting > 1 ? 's' : ''} inferred (small account, nobody senior on file)
                </Hint>
              )}
              {t.scope === 'fac' && t.apex && (
                <Hint>apex: {t.apex.person.full_name} — picked because {t.apex.basis}</Hint>
              )}
            </div>
          </div>
        )}

        {/* Toolbar: three GROUPS, not seven identical buttons. They do three different kinds of
            thing — change the zoom, change what is expanded, change what is IN the chart — and the
            last is a state toggle, so it is the only one that can look switched on. */}
        {t && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px var(--gut)', borderBottom: HAIR, flex: '0 0 auto' }}>
            <Btn sm title="Zoom out" onClick={() => setView((v) => ({ ...v, k: Math.max(0.12, v.k / 1.25) }))}>−</Btn>
            <span className="num t-micro" style={{ width: 38, textAlign: 'center', color: 'var(--dim)' }}>{Math.round(view.k * 100)}%</span>
            <Btn sm title="Zoom in" onClick={() => setView((v) => ({ ...v, k: Math.min(2.6, v.k * 1.25) }))}>+</Btn>
            <Btn sm title="Fit the whole chart in view" onClick={fitChart}>Fit</Btn>
            <span style={{ width: 1, height: 16, background: 'var(--line)' }} />
            <Btn sm onClick={() => { if (!root) return; walkAll(root, (n) => { n.open = true; openState.current[keyOf(n)] = true; }); setNudge((x) => x + 1); requestAnimationFrame(fitChart); }}>Expand all</Btn>
            <Btn sm onClick={() => { if (!root) return; walkAll(root, (n) => { if (n.depth >= 2) { n.open = false; openState.current[keyOf(n)] = false; } }); setNudge((x) => x + 1); requestAnimationFrame(fitChart); }}>Collapse</Btn>
            {ghosts > 0 && (
              <>
                <span style={{ width: 1, height: 16, background: 'var(--line)' }} />
                <Btn sm on={showGhosts} onClick={() => setShowGhosts(!showGhosts)}
                  title="People on the roster with no email and no phone. Hidden by default so the chart only shows people a rep can actually reach.">
                  Show {N(ghosts)} with no contact details
                </Btn>
              </>
            )}
          </div>
        )}

        <div
          ref={wrapRef}
          onMouseDown={(e) => {
            if ((e.target as Element).closest('[data-node]')) return;
            drag.current = { x: e.clientX, y: e.clientY, tx: view.tx, ty: view.ty };
          }}
          style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden', position: 'relative', cursor: 'grab', background: 'var(--s0)' }}
        >
          <svg width="100%" height="100%" style={{ display: 'block' }}>
            <g transform={`translate(${view.tx} ${view.ty}) scale(${view.k})`}>
              {/* Elbow connectors, drawn first so nodes sit on top of them. */}
              {edges.map(([p, c], i) => {
                const y0 = p.y + nodeH(p), y1 = c.y, mid = y0 + (y1 - y0) * 0.5;
                return (
                  <path key={i} d={`M${p.cx} ${y0} V${mid} H${c.cx} V${y1}`}
                    fill="none"
                    stroke={c.edge === 'solid' ? 'var(--line3)' : c.edge === 'dashed' ? 'var(--line2)' : 'var(--line)'}
                    strokeWidth={c.edge === 'solid' ? 1.4 : 1}
                    strokeDasharray={c.edge === 'dashed' ? '4 4' : undefined} />
                );
              })}
              {nodes.map((n) => <ChartNode key={n.id} n={n} onClick={() => onNode(n)} />)}
            </g>
          </svg>
          {nodes.length <= 1 && t && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--dim)' }}>
              Nobody matches this lens here.
            </div>
          )}
        </div>

        {/* The site index: the bridge from the corporate chart to the plant charts. Ranked
            committee-first so the sellable buildings lead, because that is the order a rep works. */}
        {t && t.scope === 'acct' && t.sites && t.sites.length > 0 && (
          <div style={{ flex: '0 0 auto', maxHeight: 220, overflowY: 'auto', borderTop: HAIR }}>
            <Section label="Sites — click one for that building's full org"
              right={<Hint>{N(t.sites.length)} plants with people on file · {N(t.sites.filter((s) => s.committee === 4).length)} with all four buying roles · ranked committee first</Hint>}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {t.sites.map((s) => (
                  <div key={s.id} onClick={() => { setScope('fac'); setKey(String(s.id)); }}
                    style={{ border: HAIR, padding: 8, width: 240, cursor: 'pointer' }}>
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.site || '(unnamed site)'}</div>
                    <Hint>{s.city || ''}{s.state ? ', ' + s.state : ''} · {SQ(s.sqft)} sq ft</Hint>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 5 }}>
                      <Meter n={s.committee} />
                      <span className="num t-micro" style={{ color: 'var(--dim2)' }}>{s.committee}/4</span>
                      <Chip tone={s.reach ? 'ok' : 'bad'}>{N(s.reach)} reachable</Chip>
                      {s.tier === '1' && <Chip tone="warn">T1</Chip>}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        )}
      </section>
    </div>
  );
}

const EdgeSwatch = ({ dashed }: { dashed?: boolean }) => (
  <svg width="18" height="6" style={{ verticalAlign: 'middle', marginRight: 4 }}>
    <line x1="0" y1="3" x2="18" y2="3" stroke={dashed ? 'var(--line2)' : 'var(--line3)'}
      strokeWidth={dashed ? 1 : 1.4} strokeDasharray={dashed ? '4 4' : undefined} />
  </svg>
);

function ChartNode({ n, onClick }: { n: Node; onClick: () => void }) {
  const p = n.p;
  const H = nodeH(n);
  const x = n.cx - NODE_W / 2;
  const ttl = p ? (p.title || '(no title)') : (n.title || '');
  const lines = wrapTitle(ttl, 30, 2);
  return (
    <g data-node={n.id} transform={`translate(${x} ${n.y})`} onClick={onClick} style={{ cursor: 'pointer' }}>
      <rect width={NODE_W} height={H} fill="var(--s2)" stroke={n.cls === 'apex' ? 'var(--line3)' : 'var(--line)'} strokeWidth="1" />
      {/* A ROLE STRIPE down the left edge of every person card. The chart is a wall of identical
          slate cards and the one thing a rep scans for is who the decision makers are. Four states,
          four colours — the SAME four the role chips use everywhere else in the app. Cards with
          nobody in them get none. */}
      {p && <rect width="4" height={H - 12} x="0" y="6" fill={ROLE_COLOR[p.icp_role || ''] || '#2A313C'} />}

      {/* The TITLE is the readable element at altitude — that is the explicit requirement — and the
          person is an initial. A name is unreadable zoomed out and useless for understanding a
          function. */}
      {lines.map((ln, i) => (
        <text key={i} x={9} y={(p ? 35 : 32) + i * (p ? 14 : 13)} fontSize="12" fontWeight="600" fill="var(--ink)">{ln}</text>
      ))}
      <text x={9} y={16} fontSize="10.5" fill="var(--dim)">{p ? initials(p.full_name) : (n.ini || '')}</text>

      {p ? (
        <>
          {/* Role and level are both RIGHT-anchored: the name went from "J.F." to "Jim F." and would
              have run straight into a chip fixed at x:48. */}
          <text x={NODE_W - 9} y={16} textAnchor="end" fontSize="10" fill="var(--dim2)">L{p.level}</text>
          <text x={NODE_W - 30} y={16} textAnchor="end" fontSize="9" fontWeight="600"
            fill={ROLE_COLOR[p.icp_role || ''] || 'var(--dim2)'}>{ROLE_SHORT[p.icp_role || ''] || ''}</text>
          {/* The reach line. This is what a rep is here for, so it is ON the card, not in a tooltip,
              and it is bottom-anchored at a FIXED y rather than pushed down by the title — a rep
              scans this chart down the reach lines, and when those lines moved with the length of
              each job title the column had no line to scan. */}
          <text x={9} y={66} fontSize="10" fontFamily="var(--mono)" fill={p.email ? 'var(--bl)' : 'var(--dim2)'}>
            {p.email ? clipTo(p.email, 30) : 'no email on file'}
          </text>
          <text x={9} y={78} fontSize="10" fontFamily="var(--mono)" fill={p.phone ? 'var(--ok)' : 'var(--dim2)'}>
            {p.phone ? p.phone + (p.switchboard ? '  switchboard' : '') : 'no phone on file'}
          </text>
          {n.acting && <text x={NODE_W - 9} y={H - 6} textAnchor="end" fontSize="9" fontWeight="600" fill="var(--am)">ACTING HEAD</text>}
          <title>
            {`${p.full_name} — ${p.title}\n${p.dept} · level ${p.level}`
              + `${p.level_adj_why ? ' (capped: ' + p.level_adj_why + ')' : ''}`
              + `\n${p.email || 'no email'} · ${p.phone || 'no phone'}${p.switchboard ? ' (switchboard, not a direct line)' : ''}`
              + `${n.acting ? '\nacting head: ' + n.acting : ''}\nclick for the full record`}
          </title>
        </>
      ) : n.meta ? (
        <text x={n.ini ? 34 : 9} y={15} fontSize="10" fill="var(--dim)">{n.meta}</text>
      ) : null}

      {/* Expander: only where there IS something to expand, so no control on screen does nothing. */}
      {n.children.length > 0 && n.kind !== 'more' && (
        <>
          <rect x={NODE_W / 2 - 11} y={H - 1} width="22" height="15" fill="var(--s3)" stroke="var(--line)" />
          <text x={NODE_W / 2} y={H + 10} textAnchor="middle" fontSize="10" fill="var(--dim)">
            {n.open === false ? '+' + (n.children.length > 99 ? '' : n.children.length) : '–'}
          </text>
        </>
      )}
    </g>
  );
}
